import { useEffect, useState } from "react";
import {
  APIProvider,
  Map,
  AdvancedMarker,
  InfoWindow,
  useMap,
} from "@vis.gl/react-google-maps";
import { ExternalLink } from "lucide-react";

const MAP_ID = "uberdoc_specialists_map";
const DEFAULT_ZOOM = 11;

export type MapSpecialist = {
  id: string;
  name: string;
  lat: number;
  lng: number;
  specialty?: string;
  price?: number;
  address?: string;
  profilePictureUrl?: string;
  initials?: string;
  profileHref?: string;
};

type Props = {
  specialists: MapSpecialist[];
  selectedId?: string | null;
  hoveredId?: string | null;
  onMarkerClick?: (id: string) => void;
  onMarkerHover?: (id: string | null) => void;
  className?: string;
  singleZoom?: number;
};

function formatPrice(price: number) {
  return price >= 1000 ? `$${(price / 1000).toFixed(1)}k` : `$${price}`;
}

function MapBoundsController({
  specialists,
  selectedId,
  hoveredId,
  singleZoom,
}: {
  specialists: MapSpecialist[];
  selectedId?: string | null;
  hoveredId?: string | null;
  singleZoom: number;
}) {
  const map = useMap();

  useEffect(() => {
    if (!map || specialists.length === 0) return;
    if (specialists.length === 1) {
      map.setCenter({ lat: specialists[0].lat, lng: specialists[0].lng });
      map.setZoom(singleZoom);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    specialists.forEach((s) => bounds.extend({ lat: s.lat, lng: s.lng }));
    map.fitBounds(bounds, 64);
  }, [map, specialists, singleZoom]);

  // Pan to the location currently being hovered or selected from the list.
  const focusId = hoveredId ?? selectedId;
  useEffect(() => {
    if (!map || !focusId) return;
    const target = specialists.find((s) => s.id === focusId);
    if (!target) return;
    map.panTo({ lat: target.lat, lng: target.lng });
  }, [map, focusId, specialists]);

  return null;
}

function PriceChip({ label, active }: { label: string; active: boolean }) {
  const base =
    "inline-flex items-center justify-center rounded-full px-2.5 py-1 text-[11px] font-semibold leading-none shadow-md ring-1 transition-all duration-150 select-none whitespace-nowrap";
  const styles = active
    ? "bg-secondary text-white ring-secondary scale-110"
    : "bg-white text-foreground ring-black/10 hover:ring-secondary/60";
  return <div className={`${base} ${styles}`}>{label}</div>;
}

export function SpecialistMap({
  specialists,
  selectedId,
  hoveredId,
  onMarkerClick,
  onMarkerHover,
  className,
  singleZoom = 14,
}: Props) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    if (selectedId !== undefined) setActiveId(selectedId ?? null);
  }, [selectedId]);

  if (!apiKey || specialists.length === 0) return null;

  const fallbackCenter = { lat: specialists[0].lat, lng: specialists[0].lng };
  const active = activeId ? specialists.find((s) => s.id === activeId) : null;

  return (
    <APIProvider apiKey={apiKey}>
      <div className={className}>
        <Map
          mapId={MAP_ID}
          defaultCenter={fallbackCenter}
          defaultZoom={specialists.length === 1 ? singleZoom : DEFAULT_ZOOM}
          gestureHandling="greedy"
          disableDefaultUI
          zoomControl
          clickableIcons={false}
          className="w-full h-full"
        >
          <MapBoundsController
            specialists={specialists}
            selectedId={selectedId}
            hoveredId={hoveredId}
            singleZoom={singleZoom}
          />
          {specialists.map((s) => {
            const isActive = activeId === s.id || hoveredId === s.id;
            const label =
              typeof s.price === "number" && s.price > 0
                ? formatPrice(s.price)
                : (s.initials ?? "•");
            return (
              <AdvancedMarker
                key={s.id}
                position={{ lat: s.lat, lng: s.lng }}
                title={s.name}
                zIndex={isActive ? 1000 : 1}
                onClick={() => {
                  setActiveId(s.id);
                  onMarkerClick?.(s.id);
                }}
              >
                <div
                  onMouseEnter={() => onMarkerHover?.(s.id)}
                  onMouseLeave={() => onMarkerHover?.(null)}
                >
                  <PriceChip label={label} active={isActive} />
                </div>
              </AdvancedMarker>
            );
          })}
          {active && (
            <InfoWindow
              position={{ lat: active.lat, lng: active.lng }}
              onCloseClick={() => setActiveId(null)}
              pixelOffset={[0, -28]}
              headerDisabled
            >
              <div className="w-[240px] p-3 font-sans">
                <div className="flex items-start gap-3">
                  {active.profilePictureUrl ? (
                    <img
                      src={active.profilePictureUrl}
                      alt={active.name}
                      className="w-11 h-11 rounded-full object-cover shrink-0 ring-1 ring-black/5"
                    />
                  ) : (
                    <div className="w-11 h-11 rounded-full bg-muted text-primary font-semibold flex items-center justify-center text-sm shrink-0">
                      {active.initials ?? active.name[0]}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm text-foreground leading-tight truncate">
                      {active.name}
                    </p>
                    {active.specialty && (
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {active.specialty}
                      </p>
                    )}
                    {typeof active.price === "number" && active.price > 0 && (
                      <p className="text-xs font-semibold text-foreground mt-1">
                        {formatPrice(active.price)}
                        <span className="text-muted-foreground font-normal">
                          {" "}
                          / visit
                        </span>
                      </p>
                    )}
                  </div>
                </div>
                {active.address && (
                  <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                    {active.address}
                  </p>
                )}
                {active.profileHref && (
                  <a
                    href={active.profileHref}
                    className="mt-2.5 inline-flex items-center gap-1 text-xs font-semibold text-secondary hover:underline"
                  >
                    View profile
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </InfoWindow>
          )}
        </Map>
      </div>
    </APIProvider>
  );
}
